import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API, getToken, STATUS_LABELS, STATUS_ORDER } from "@/lib/api";
import { StatusPill, OverdueBadge } from "@/components/app/Badges";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search, AlertTriangle, Download, FileDown, X } from "lucide-react";

export default function DashboardPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateField, setDateField] = useState("complaint_date");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const buildParams = () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (overdueOnly) params.overdue = true;
    if (q.trim()) params.q = q.trim();
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (dateFrom || dateTo) params.date_field = dateField;
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, statsRes] = await Promise.all([
        api.get("/complaints", { params: buildParams() }),
        api.get("/stats"),
      ]);
      setItems(data);
      setStats(statsRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [statusFilter, overdueOnly, dateFrom, dateTo, dateField]);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const clearFilters = () => {
    setQ("");
    setStatusFilter("all");
    setOverdueOnly(false);
    setDateFrom("");
    setDateTo("");
    setDateField("complaint_date");
  };

  const onExportCSV = () => {
    const params = new URLSearchParams();
    const p = buildParams();
    Object.entries(p).forEach(([k, v]) => params.append(k, String(v)));
    params.append("auth", getToken());
    window.open(`${API}/complaints/export?${params.toString()}`, "_blank");
  };

  const onExportPDF = () => {
    const params = new URLSearchParams();
    const p = buildParams();
    Object.entries(p).forEach(([k, v]) => params.append(k, String(v)));
    params.append("auth", getToken());
    window.open(`${API}/complaints/export.pdf?${params.toString()}`, "_blank");
  };

  const totals = stats || { total: 0, overdue: 0, by_status: {} };

  const metricCards = useMemo(
    () => [
      { key: "total", label: "Total reclamaciones", value: totals.total, accent: "black" },
      { key: "overdue", label: "Tiempo excedido", value: totals.overdue, accent: "red" },
      {
        key: "pendiente",
        label: "Pendientes",
        value: totals.by_status?.pendiente || 0,
        accent: "black",
      },
      {
        key: "resuelta",
        label: "Resueltas",
        value: totals.by_status?.resuelta || 0,
        accent: "black",
      },
    ],
    [totals]
  );

  const hasActiveFilters =
    q || statusFilter !== "all" || overdueOnly || dateFrom || dateTo;

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-[1500px] mx-auto" data-testid="dashboard-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
            Panel de operaciones
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            Reclamaciones
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={onExportCSV}
            variant="outline"
            className="rounded-none border-black h-11 px-4 text-xs uppercase tracking-[0.2em]"
            data-testid="export-csv-btn"
          >
            <Download size={16} className="mr-2" /> CSV
          </Button>
          <Button
            onClick={onExportPDF}
            variant="outline"
            className="rounded-none border-black h-11 px-4 text-xs uppercase tracking-[0.2em]"
            data-testid="export-pdf-btn"
          >
            <FileDown size={16} className="mr-2" /> PDF
          </Button>
          <Button
            onClick={() => navigate("/complaints/new")}
            className="rounded-none btn-primary h-11 px-5 text-xs uppercase tracking-[0.2em]"
            data-testid="new-complaint-cta"
          >
            <Plus size={16} className="mr-2" /> Nueva reclamación
          </Button>
        </div>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-black mb-8">
        {metricCards.map((m, i) => (
          <div
            key={m.key}
            className={`p-6 ${
              i < metricCards.length - 1 ? "border-r border-black" : ""
            } ${m.accent === "red" ? "bg-[#FFEBEB]" : "bg-white"}`}
            data-testid={`metric-${m.key}`}
          >
            <div
              className={`text-[10px] uppercase tracking-[0.25em] mb-3 ${
                m.accent === "red" ? "text-[#E63946]" : "text-zinc-500"
              }`}
            >
              {m.label}
            </div>
            <div
              className={`font-display text-5xl font-bold ${
                m.accent === "red" ? "text-[#E63946]" : "text-black"
              }`}
            >
              {m.value}
            </div>
          </div>
        ))}
      </section>

      <section className="border border-black bg-white">
        <div className="border-b border-black p-4 flex flex-col gap-3">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <form onSubmit={onSearch} className="flex gap-2 flex-1 max-w-xl">
              <div className="relative flex-1">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
                />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar por número o conductor…"
                  className="rounded-none border-black pl-9 h-10"
                  data-testid="search-input"
                />
              </div>
              <Button
                type="submit"
                className="rounded-none btn-primary h-10 px-4 text-xs uppercase tracking-widest"
                data-testid="search-submit"
              >
                Buscar
              </Button>
            </form>

            <div className="flex items-center gap-3 flex-wrap">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger
                  className="rounded-none border-black h-10 w-[210px] text-xs uppercase tracking-widest"
                  data-testid="status-filter-select"
                >
                  <SelectValue placeholder="Filtrar estado" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {STATUS_ORDER.map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <button
                type="button"
                onClick={() => setOverdueOnly((v) => !v)}
                className={`h-10 px-4 text-xs uppercase tracking-widest border flex items-center gap-2 transition-colors ${
                  overdueOnly
                    ? "bg-[#E63946] text-white border-[#E63946]"
                    : "bg-white text-[#E63946] border-[#E63946] hover:bg-[#FFEBEB]"
                }`}
                data-testid="overdue-toggle"
              >
                <AlertTriangle size={14} />{" "}
                {overdueOnly ? "Excedidas activas" : "Solo excedidas"}
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-3 pt-3 border-t border-zinc-200">
            <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 shrink-0">
              Rango de fechas
            </span>
            <Select value={dateField} onValueChange={setDateField}>
              <SelectTrigger
                className="rounded-none border-black h-10 w-[200px] text-xs uppercase tracking-widest"
                data-testid="date-field-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="complaint_date">Fecha reclamación</SelectItem>
                <SelectItem value="deadline">Fecha límite</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-none border-black h-10 w-[170px]"
                data-testid="date-from-input"
              />
              <span className="text-zinc-400 text-xs">→</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-none border-black h-10 w-[170px]"
                data-testid="date-to-input"
              />
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="ml-auto text-[11px] uppercase tracking-widest text-zinc-600 hover:text-black inline-flex items-center gap-1"
                data-testid="clear-filters-btn"
              >
                <X size={12} /> Limpiar filtros
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="ds-table w-full" data-testid="complaints-table">
            <thead>
              <tr>
                <th>Número</th>
                <th>Conductor</th>
                <th>Fecha reclamación</th>
                <th>Fecha límite</th>
                <th>Estado</th>
                <th>Evidencias</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center text-zinc-500 py-12">
                    Cargando…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-zinc-500 py-16">
                    Sin reclamaciones aún. Crea la primera.
                  </td>
                </tr>
              ) : (
                items.map((c) => (
                  <tr
                    key={c.id}
                    className={c.overdue ? "row-overdue" : ""}
                    data-testid={`complaint-row-${c.number}`}
                  >
                    <td className="font-mono font-semibold">{c.number}</td>
                    <td>{c.driver_name}</td>
                    <td>{c.complaint_date}</td>
                    <td className={c.overdue ? "text-overdue font-semibold" : ""}>
                      {c.deadline}
                    </td>
                    <td>
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusPill status={c.status} />
                        {c.overdue && <OverdueBadge />}
                      </div>
                    </td>
                    <td>{c.photos?.length || 0}</td>
                    <td className="text-right">
                      <Link
                        to={`/complaints/${c.id}`}
                        className="text-xs uppercase tracking-widest font-semibold underline underline-offset-4 hover:text-zinc-500"
                        data-testid={`open-complaint-${c.number}`}
                      >
                        Abrir →
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
